import React, { useEffect } from 'react';
import Modal from 'react-modal';
import { embedUrlFrom, watchUrlFrom } from './video';
import { openExternal } from '../platform';
import '../styles/ExerciseVideoModal.css';

/**
 * YouTube demo player.
 *
 * Three defects fixed here:
 *
 *  - `isOpen` was required but EditProgram never passed it, so react-modal
 *    received undefined and the player never opened. Callers that mount this
 *    conditionally can omit it: it defaults to true, since a mounted modal is
 *    meant to be visible.
 *  - The iframe had no `title`, which fails jsx-a11y/iframe-has-title and leaves
 *    screen-reader users with an unlabelled frame.
 *  - It imported train.css and used `.close-modal` and `.back-to-training-btn`,
 *    which the train.css rewrite removed, so its buttons were unstyled. It now
 *    has its own stylesheet built on the lucky-health primitives and does not
 *    depend on another screen's CSS.
 */
const ExerciseVideoModal = ({ isOpen = true, onClose, videoUrl, title }) => {
    // Parsing lives in ./video.js now. It used to be inline here AND inline in
    // train.js, with two different expressions that disagreed about the same
    // link — see that module's header.
    const embedUrl = embedUrlFrom(videoUrl);
    const watchUrl = watchUrlFrom(videoUrl);

    // Escape closes it — even when react-modal's own handler cannot.
    //
    // react-modal listens for Escape on the modal content, so it only fires
    // while focus is inside the modal. Here focus almost never is: the video is
    // an iframe, and the moment it loads (or the trigger button keeps focus) the
    // key goes elsewhere and Escape does nothing — the exact defect an
    // exploratory pass caught. A document-level listener catches it wherever
    // focus sits in this document. A cross-origin iframe that has taken focus is
    // still beyond reach — the browser's boundary, not ours — so the × and the
    // backdrop stay as the always-available close.
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="video-modal-panel"
            overlayClassName="video-modal-backdrop"
            contentLabel={title ? `${title} demo video` : 'Exercise demo video'}
            // No #root is registered with react-modal, and warning about it on
            // every open is noise; the overlay already traps interaction.
            ariaHideApp={false}
        >
            <div className="video-modal-head">
                {title && <h2 className="video-modal-title">{title}</h2>}
                <button onClick={onClose} className="icon-button" aria-label="Close video">
                    ✕
                </button>
            </div>

            {embedUrl ? (
                <div className="video-frame">
                    <iframe
                        src={embedUrl}
                        title={title ? `${title} demo` : 'Exercise demo'}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>
            ) : (
                // An unparseable link previously produced an embed URL ending in
                // "/null" and a silently broken player.
                <p className="status-error">No demo video is available for this exercise.</p>
            )}

            <div className="video-modal-foot">
                {/* Shown whenever we hold a readable link, not only when the
                    embed fails — a refused cross-origin iframe reports nothing
                    we can branch on. */}
                {watchUrl && (
                    <a
                        className="video-modal-watch"
                        href={watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        // On the web this is a plain new-tab link. In the native
                        // app, target="_blank" would hand the tap to Capacitor's
                        // own WKWebView delegate, which backgrounds the whole app
                        // into standalone Safari (see platform.js's openExternal
                        // doc comment) — so there, and only there, this opens an
                        // in-app sheet instead and the anchor's own handling is
                        // prevented from also firing.
                        onClick={(e) => {
                            if (openExternal(watchUrl)) e.preventDefault();
                        }}
                    >
                        Watch on YouTube
                    </a>
                )}
                <button className="btn-primary" onClick={onClose}>
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default ExerciseVideoModal;
